import { useMemo } from "react";
import { generateThemeColors } from "@tellescope/utils";
import type { Theming } from "../schema";

/**
 * Maps borderRadius schema values to CSS --radius values.
 * "default" is intentionally null — it leaves the globals.css value in place
 * rather than overriding it with an explicit value.
 */
const BORDER_RADIUS_VALUES: Record<NonNullable<Theming["borderRadius"]>, string | null> = {
  square: "0rem",
  default: null, // leave unset so globals.css value applies
  rounded: "1rem",
};

/**
 * Hook that generates inline styles for scoped CSS variable theming.
 *
 * Applies portal brand settings as CSS custom properties on a wrapper element
 * so they cascade to all children that reference var(--primary), var(--foreground), etc.
 * All new fields (textColor, headerColor, buttonTextColor, borderRadius, heading/body fonts)
 * were added as part of the portal branding & customization enhancement.
 *
 * CSS variables set here are intentionally scoped to the portal content wrapper
 * (OrganizationThemeProvider / PortalPreview) — they do NOT bleed into the admin UI.
 * See OrganizationThemeProvider.tsx for the scoping approach.
 *
 * @param theming - The theming configuration from the portal schema
 * @returns A React.CSSProperties object with CSS custom properties
 */
export function useThemeStyles(theming: Theming | undefined): React.CSSProperties {
  return useMemo(() => {
    const styles: Record<string, string> = {};

    // Apply primary color if set
    if (theming?.primaryColor) {
      const colors = generateThemeColors(theming.primaryColor);
      if (colors) {
        styles["--primary"] = colors.cssValue;
        // buttonTextColor overrides the auto-calculated foreground
        if (!theming.buttonTextColor) {
          styles["--primary-foreground"] = colors.foregroundCssValue;
        }
      }
    }

    // buttonTextColor explicitly sets --primary-foreground (button text), useful when
    // the primary color is too dark/light for the auto-calculated contrast color
    if (theming?.buttonTextColor) {
      const colors = generateThemeColors(theming.buttonTextColor);
      if (colors) {
        styles["--primary-foreground"] = colors.cssValue;
      }
    }

    // Apply secondary color if set
    if (theming?.secondaryColor) {
      const colors = generateThemeColors(theming.secondaryColor);
      if (colors) {
        styles["--secondary"] = colors.cssValue;
        styles["--secondary-foreground"] = colors.foregroundCssValue;
      }
    }

    // textColor sets --foreground and related tokens so shadcn's text-foreground
    // Tailwind class picks it up correctly (HSL channel values, not raw hex)
    if (theming?.textColor) {
      const colors = generateThemeColors(theming.textColor);
      if (colors) {
        styles["--foreground"] = colors.cssValue;
        styles["--card-foreground"] = colors.cssValue;
        styles["--popover-foreground"] = colors.cssValue;
        styles["--sidebar-foreground"] = colors.cssValue;
      }
    }

    // headerColor is consumed by PortalNavbar and PortalSidebar via
    // style={{ backgroundColor: "var(--portal-header-color, var(--background))" }}
    if (theming?.headerColor) {
      styles["--portal-header-color"] = theming.headerColor;
    }

    // borderRadius overrides the shadcn --radius variable used by buttons, cards, inputs, etc.
    if (theming?.borderRadius) {
      const value = BORDER_RADIUS_VALUES[theming.borderRadius];
      if (value !== null) {
        styles["--radius"] = value;
      }
    }

    // Apply font family if set (and not system default)
    if (theming?.fontFamily && theming.fontFamily !== "system") {
      styles.fontFamily = `"${theming.fontFamily}", ui-sans-serif, system-ui, sans-serif`;
    }

    // headingFont/bodyFont are consumed via var(--portal-heading-font) and
    // var(--portal-body-font) in HeadingBlock and TextBlock respectively
    if (theming?.headingFont && theming.headingFont !== "system") {
      styles["--portal-heading-font"] = `"${theming.headingFont}", ui-sans-serif, system-ui, sans-serif`;
    }

    if (theming?.bodyFont && theming.bodyFont !== "system") {
      styles["--portal-body-font"] = `"${theming.bodyFont}", ui-sans-serif, system-ui, sans-serif`;
    }

    return styles as React.CSSProperties;
  }, [
    theming?.primaryColor,
    theming?.secondaryColor,
    theming?.textColor,
    theming?.headerColor,
    theming?.buttonTextColor,
    theming?.borderRadius,
    theming?.fontFamily,
    theming?.headingFont,
    theming?.bodyFont,
  ]);
}
